import { memo, useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem, StyledBox, Input } from './SixthFlows.styled';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import SixthAnalysesList from 'components/Analyses/SixthAnalyses/List/List';
import SixthFlowPromtsList from './Prompts/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useSixthFlows from 'hooks/useSixthFlows';
import useSixthAnalyses from 'hooks/useSixthAnalyses';
import useBlob from 'hooks/useBlob';
import useChecksum from 'hooks/useChecksum';
import { DELETE_SIXTH_FLOW } from 'helpers/confirmations';
import { checkValidEmail, checkEmptyString, needToUpdateField } from 'helpers';
import { sixthFlowsSelectors } from "features/sixthFlows";
import { blobsSelectors } from 'features/blobs';

const SixthFlows = () => {
  const inputRef = useRef(null);
  const { openDeleteDialog, setSixthFlowPromptsDrawer } = useApp();
  const { setSuccess } = useSuccess();
  const { getSixthFlow, createSixthFlow, updateSixthFlow } = useSixthFlows();
  const { getSixthAnalyses, createSixthAnalysis, clearLocalSixthAnalyses } = useSixthAnalyses();
  const { processBlob } = useBlob();
  const { processFiles } = useChecksum();
  const id = useCustomSelector(sixthFlowsSelectors.getSixthFlowId);
  const sixthFlow = useCustomSelector(sixthFlowsSelectors.getSixthFlowData);
  const fetching = useCustomSelector(sixthFlowsSelectors.sixthFlowIsFetching);
  const blobFetching = useCustomSelector(blobsSelectors.blobIsFetching);
  const [email, setEmail] = useState({ value: '', error: '' });
  const [promptTitle, setPromptTitle] = useState({ value: '', error: '' });
  const [promptText, setPromptText] = useState({ value: '', error: '' });
  const [files, setFiles] = useState([]);
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (sixth_flow) => {
    setEmail({ value: sixth_flow?.email, error: '' });
  };
  const handleExited = () => {
    setEmail({ value: '', error: '' });
    setEdit(false);
    setPromptTitle({ value: '', error: '' });
    setPromptText({ value: '', error: '' });
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkValidEmail(email.value)) {
      setEmail(prev => ({ ...prev, error: `Email is invalid` }) );
      hasError = true;
    }
    if (!id && !checkEmptyString(promptTitle.value)) {
      setPromptTitle(prev => ({ ...prev, error: `Title can't be empty` }) );
      hasError = true;
    }
    if (!id && !checkEmptyString(promptText.value)) {
      setPromptText(prev => ({ ...prev, error: `Text can't be empty` }) );
      hasError = true;
    }
    if (hasError) return;

    const sixth_flow = {
      ...(needToUpdateField(sixthFlow?.email, email.value) && {  email: email.value }),
      ...(!id && { sixth_prompts_attributes: [{ 
            title: promptTitle.value,
            text: promptText.value
          }]})
    };

    if (!_isEmpty(sixth_flow)) {
      if (!!id) {
        updateSixthFlow(sixth_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createSixthFlow(sixth_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_SIXTH_FLOW });
  const handleUploadClick = () => inputRef.current.click();
  const handleFileChange = ({ target: { files } }) => {
    processFiles(files, ({ file, checksum }) =>
      processBlob({ file, checksum }, blob => { 
        setFiles(prev => [...prev, { id: blob.id, filename: blob.filename, content_type: blob.content_type }])
      })
    );
  };
  const onDeleteFile = (id) => {
    const filteredFiles = _filter(files, file => file.id !== id);
    setFiles(filteredFiles);
  };
  const handleCreateSeconAnalysis = () => {
    const sixth_analysis = { 
      inputs: _map(files, (file) => file.id)
    };
    createSixthAnalysis(sixth_analysis)
    .then(({ error }) => {
      if (!error) {
        setFiles([]);
      }
    })
  };
  const fetchSixthAnalyses = useCallback(() => {
    getSixthAnalyses({ offset: 0 })
  }, [getSixthAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalSixthAnalyses();
    };
  }, [id, clearLocalSixthAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getSixthFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { sixth_flow } = payload;
          handleEntering(sixth_flow);
          fetchSixthAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getSixthFlow, fetchSixthAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label='Email'
              disabled={fetching || (!!id && !edit)}
              value={email.value}
              onChange={({ target: { value } }) => setEmail({ value, error: '' })}
              helperText={email.error}
              error={!!email.error}
            />
            {!id && <>
              <TextField
                fullWidth
                label='Prompt Title'
                helperText={promptTitle.error}
                error={!!promptTitle.error}
                value={promptTitle.value}
                onChange={({ target: { value } }) => setPromptTitle({ value, error: '' })}
              />
              <TextField
                fullWidth
                label='Prompt Text'
                multiline
                minRows={5}
                maxRows={8}
                helperText={promptText.error}
                error={!!promptText.error}
                value={promptText.value}
                onChange={({ target: { value } }) => setPromptText({ value, error: '' })}
              />   
            </>}
          </CardItem>
          {!!id && <>
            <CardItem>
              <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Prompts</Typography>
              <Box>
                <Button 
                  variant='outlined' 
                  disabled={fetching || !id} 
                  onClick={() => setSixthFlowPromptsDrawer({ open: true, edit: false, id: '', title: '', text: '' })}
                >
                  Add
                </Button>
              </Box>
            </CardItem>
            <StyledBox>
              <SixthFlowPromtsList />
            </StyledBox>
          </>}
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Upload</Typography>
            {files.length !== 0 && <Button variant='outlined' disabled={fetching || blobFetching} sx={{ mr: '5px' }} onClick={handleCreateSeconAnalysis}>Send</Button>}
          </CardItem>
          <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center'>
            <Button 
              color='primary' 
              startIcon={<AddIcon />} 
              endIcon={blobFetching && <CircularProgress size={20} color='inherit' />}
              disabled={blobFetching || !id} 
              onClick={handleUploadClick}
            >
              PDF FILE
            </Button>
            {files.length !== 0 && _map(files, ({ id, filename }) => (
              <Box key={id} display='flex' alignItems='center' mt={1} mb={1}>
                <Typography noWrap align='left' sx={{ flexGrow: 1, pl: 1 }}>{filename}</Typography>
                <IconButton
                  disabled={blobFetching}
                  onClick={() => onDeleteFile(id)}
                  color='error'
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Input
                ref={inputRef}
                multiple
                accept=".pdf"
                id="documents"
                type="file"
                onClick={(event) => event.target.value = ''}
                onChange={handleFileChange}
            />
          </Box>
        </Card>  
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <SixthAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <SixthFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);